export type ShortcutScope = "workspace" | "list" | "search";

export type Shortcut = {
  keys: readonly string[];
  label: string;
  scope: ShortcutScope;
};

// Every keyboard shortcut in the app. `keys` uses react-hotkeys-hook syntax,
// one entry per alternative; `mod` binds both ⌘ and Ctrl and displays as the
// platform's modifier. Search navigation and opening are cmdk's own keys, so
// they are listed for the ⌘K footer but never bound here.
export const shortcuts = {
  openSearch: { keys: ["mod+k"], label: "Search", scope: "workspace" },
  focusListSearch: { keys: ["/"], label: "Search", scope: "list" },
  openFilters: { keys: ["f"], label: "Filter", scope: "list" },
  navigateResults: { keys: ["up", "down"], label: "Navigate", scope: "search" },
  openResult: { keys: ["enter"], label: "Open", scope: "search" },
  nextSearchTab: { keys: ["tab"], label: "Switch tab", scope: "search" },
  loadMoreResults: { keys: ["mod+enter"], label: "More results", scope: "search" },
  closeSearch: { keys: ["esc"], label: "Close", scope: "search" },
} as const satisfies Record<string, Shortcut>;

export type ShortcutId = keyof typeof shortcuts;

const KEY_SYMBOLS: Record<string, string> = {
  meta: "⌘",
  ctrl: "Ctrl",
  alt: "⌥",
  shift: "⇧",
  enter: "↵",
  tab: "⇥",
  esc: "esc",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
};

export type ShortcutPlatform = "mac" | "other";

/** The keycaps a hint shows: each alternative's keys, with `mod` as ⌘ on Mac and Ctrl elsewhere. */
export function shortcutKeycaps(id: ShortcutId, platform: ShortcutPlatform): string[] {
  return shortcuts[id].keys.flatMap(combo =>
    combo.split("+").map((key) => {
      if (key === "mod") return platform === "mac" ? "⌘" : "Ctrl";
      return KEY_SYMBOLS[key] ?? key.toUpperCase();
    }));
}

/** The react-hotkeys-hook binding, with `mod` expanded so ⌘ and Ctrl both fire on every platform. */
export function shortcutHotkeys(id: ShortcutId): string {
  return shortcuts[id].keys
    .flatMap(combo => combo.includes("mod+") ? [combo.replace("mod+", "meta+"), combo.replace("mod+", "ctrl+")] : [combo])
    .join(",");
}

/** The `aria-keyshortcuts` value for a shortcut. */
export function shortcutAriaKeys(id: ShortcutId): string {
  const names: Record<string, string> = { meta: "Meta", ctrl: "Control", enter: "Enter", tab: "Tab", esc: "Escape", up: "ArrowUp", down: "ArrowDown" };
  return shortcutHotkeys(id)
    .split(",")
    .map(combo => combo.split("+").map(key => names[key] ?? key.toUpperCase()).join("+"))
    .join(" ");
}
